/* Four pricing decisions, shared with the Mini app. Local confirmations
   precede assigned review; approval starts the client proposal. */

import { useAuth } from '../auth/AuthContext';
import { stageAccess } from './stageAccess';
import { architectureReviewView } from './architectureReview';
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  architectApi, PricingMethodology, PricingMonthlyRow, PricingPlan,
  PricingSheet, PricingSheetResponse,
} from './api';
import {
  DEFAULT_RULES, casePricingEnabled, caseSaleTotal, invalidatePricingSections, pricingSectionConfirmed,
} from './pricingMath';
import MonthlyRecommendationPanel from './MonthlyRecommendationPanel';
import PricingReviewFlow, { PricingReviewStep, PRICING_REVIEW_STEPS } from './PricingReviewFlow';
import MonthlyServicesEditor from './MonthlyServicesEditor';
import { monthlyRowsReady } from './monthlyReviewState';
import './pricing-workspace.css';
import './pricing-refinement.css';
import { anchorRevision, useCommentDocument, commentBlockAttributes, CommentBlockButton } from './CommentAnchors';
import { AdminUser, listUsers } from '../auth/api';
import { proposalApi } from './proposalApi';
import { FileText, SlidersHorizontal, Save, UserRound } from 'lucide-react';
import { StageHeader, StageFooter, StageAiButton } from './StageChrome';
import ApproveAsSelector, { useApprovalActor } from './ApproveAsSelector';

interface Props {
  open: boolean;
  projectId: string;
  projectName: string;
  onClose: () => void;
  onProceed?: () => void;
  presentation?: 'modal' | 'workspace';
  active?: boolean;
  refreshKey?: number;
  onChanged?: () => void;
  onDraftStateChange?: (state: { dirty: boolean; busy: boolean }) => void;
  currentUserId?: string;
  canManagePricing?: boolean;
  referenceOpen?: boolean;
  onToggleReference?: () => void;
  reference?: React.ReactNode;
}

const EMPTY: PricingSheet = { setup: [], monthly: [] };


export default function PricingPanel({ open, projectId, projectName, onClose, onProceed, presentation = 'modal', active = true, refreshKey = 0, onChanged, onDraftStateChange, currentUserId, canManagePricing = false, referenceOpen = false, onToggleReference, reference }: Props) {
  const { user, permissions } = useAuth();
  const rights = stageAccess(user, permissions, 'pricing');
  const rightsRef = useRef(rights); rightsRef.current = rights;
  const workspace = presentation === 'workspace';
  const saveImpactId = useId();
  const bodyRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<PricingReviewStep>('development');
  const [monthlyVisited, setMonthlyVisited] = useState(false);
  const [suggestionsProject, setSuggestionsProject] = useState<string | null>(null);
  const suggestionsOpen = suggestionsProject === projectId;
  const setSuggestionsOpen = (value: boolean) => setSuggestionsProject(value ? projectId : null);
  const [monthlySourceKey, setMonthlySourceKey] = useState('');
  const [sourceOpen, setSourceOpen] = useState(false);
  const [caseFocus, setCaseFocus] = useState<{ path: string | null } | null>(null);
  const [meta, setMeta] = useState<PricingSheetResponse | null>(null);
  const [sheet, setSheetState] = useState<PricingSheet>(EMPTY);
  const comments = useCommentDocument('pricing', projectId, anchorRevision(sheet), anchor => {
    const target = anchor.path?.startsWith('monthly.') ? 'monthly' : 'development';
    if (target === 'monthly') { setMonthlyVisited(true); } else { setSourceOpen(true); setCaseFocus({ path: anchor.path || null }); }
    if (target === step) return false;
    setStep(target); return true;
  });
  const [methodology, setMethodology] = useState<PricingMethodology | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [changesReason, setChangesReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [recoveringProposal, setRecoveringProposal] = useState(false);
  const [repriceFailure, setRepriceFailure] = useState<string | null>(null);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const recoveryPending = useRef(false);
  const [assigning, setAssigning] = useState(false);
  const [owners, setOwners] = useState<AdminUser[]>([]);
  const [note, setNote] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null);
  const [saveConflict, setSaveConflict] = useState(false);
  const [basisOpen, setBasisOpen] = useState(false);
  const [ownerMenuOpen, setOwnerMenuOpen] = useState(false);
  const globalAssignment = rights.configured || !!meta?.approvals?.stage_review || (meta?.approvals?.approvers.length ?? 0) > 1;
  const pricingActorOptions = useMemo(() => meta?.approvals?.approvers || (meta?.assigned_to ? [{ id: meta.assigned_to, username: meta.assigned_to_name || 'Assigned approver', display_name: meta.assigned_to_name || null }] : []), [meta?.approvals?.approvers, meta?.assigned_to, meta?.assigned_to_name]);
  const pricingActor = useApprovalActor({ root: canManagePricing, status: meta?.approvals, step: 'pricing', targetId: projectId, options: pricingActorOptions, enabled: meta?.status !== 'approved' });
  const [rateStr, setRateStr] = useState('');
  const [pctStr, setPctStr] = useState('');
  const [middleStr, setMiddleStr] = useState('');
  const [seniorStr, setSeniorStr] = useState('');
  const [savingBasis, setSavingBasis] = useState(false);
  const [assistantBusy, setAssistantBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshPending, setRefreshPending] = useState(false);
  const [confirmRefresh, setConfirmRefresh] = useState(false);
  const savedSheet = useRef(JSON.stringify(EMPTY));
  const savedBasis = useRef(JSON.stringify(['', '', '', '']));
  const editRevision = useRef(0);
  const loadRequest = useRef(0);
  const loadedProject = useRef<string | null>(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  const proceedRef = useRef(onProceed);
  proceedRef.current = onProceed;
  const proceedTimer = useRef<number | null>(null);
  const entry = useRef({ active: false, refreshKey });
  const projectReady = loadedProject.current === projectId;
  const sheetDirty = projectReady && JSON.stringify(sheet) !== savedSheet.current;
  const basisDirty = projectReady && basisOpen && JSON.stringify([rateStr, pctStr, middleStr, seniorStr]) !== savedBasis.current;
  const dirty = sheetDirty || basisDirty;
  const draftSnapshot = JSON.stringify({ sheet, basis: basisOpen ? [rateStr, pctStr, middleStr, seniorStr] : null });
  const latest = useRef({ dirty, busy: saving || savingBasis || approving || submitting || rejecting || assigning || assistantBusy || recoveringProposal, projectId, draftSnapshot });
  latest.current = { dirty, busy: saving || savingBasis || approving || submitting || rejecting || assigning || assistantBusy || recoveringProposal, projectId, draftSnapshot };
  const sheetRef = useRef(sheet);
  sheetRef.current = sheet;

  useEffect(() => {
    setRepriceFailure(null);
    setRecoveryError(null);
  }, [projectId]);

  useEffect(() => {
    if (!open || !currentUserId || !canManagePricing) return;
    let cancelled = false;
    void listUsers().then(users => {
      if (!cancelled) setOwners(users.filter(owner => !owner.isDisabled && (owner.isRoot || (owner.permissions.includes('architect:use') && owner.permissions.includes('architect:pricing')))));
    }).catch(() => { /* Pricing still works if the teammate list is unavailable. */ });
    return () => { cancelled = true; };
  }, [open, currentUserId, canManagePricing]);

  useEffect(() => () => {
    if (proceedTimer.current !== null) window.clearTimeout(proceedTimer.current);
  }, []);

  useLayoutEffect(() => {
    if (workspace && bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [step, workspace]);

  // Only user edits advance this revision. A refresh that began before an edit
  // must never adopt its response over the current draft.
  const setSheet = useCallback<React.Dispatch<React.SetStateAction<PricingSheet>>>((update) => {
    if (!rightsRef.current.edit) return;
    editRevision.current += 1;
    setSheetState(update);
  }, []);
  const editBasis = (setter: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    editRevision.current += 1;
    setter(value);
  };

  const rules = methodology?.rules ?? DEFAULT_RULES;
  const plan: PricingPlan = useMemo(() => sheet.plan ?? {
    mode: 'sequential', qa_weeks: 0, qa_engineers: 1, qa_level: 'middle',
  }, [sheet.plan]);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!rights.view) return false;
    const request = ++loadRequest.current;
    const revision = editRevision.current;
    const snapshot = latest.current.draftSnapshot;
    if (background) setRefreshing(true);
    else setLoading(true);
    try {
      const r = await architectApi.getPricing(projectId);
      if (request !== loadRequest.current || latest.current.projectId !== projectId) return false;
      if (revision !== editRevision.current || snapshot !== latest.current.draftSnapshot || latest.current.busy) {
        setRefreshPending(true);
        return false;
      }
      setMeta(r);
      setMonthlySourceKey(`${r.estimate_id || ''}:${r.source_reviewed_at || ''}`);
      const s = r.sheet || EMPTY;
      const normalized: PricingSheet = { ...s, plan: s.plan ?? { mode: 'sequential', qa_weeks: 0, qa_engineers: 1, qa_level: 'middle' } };
      savedSheet.current = JSON.stringify(normalized);
      setSheetState(normalized);
      if (r.methodology) {
        savedBasis.current = JSON.stringify([
          String(r.methodology.day_rate), String(r.methodology.monthly_pct),
          String(r.methodology.rules?.middle_week_rate ?? DEFAULT_RULES.middle_week_rate),
          String(r.methodology.rules?.senior_week_rate ?? DEFAULT_RULES.senior_week_rate),
        ]);
        setMethodology(r.methodology);
        setRateStr(String(r.methodology.day_rate));
        setPctStr(String(r.methodology.monthly_pct));
        setMiddleStr(String(r.methodology.rules?.middle_week_rate ?? DEFAULT_RULES.middle_week_rate));
        setSeniorStr(String(r.methodology.rules?.senior_week_rate ?? DEFAULT_RULES.senior_week_rate));
      }
      loadedProject.current = projectId;
      setRefreshPending(false);
      setConfirmRefresh(false);
      setNote(null);
      setSaveConflict(false);
      if (!background) setStep('development');
      return true;
    } catch (err: any) {
      if (request === loadRequest.current && latest.current.projectId === projectId) {
        if (err?.status === 409 || err?.status === 428) setSaveConflict(true);
      setNote({ text: err.message, kind: 'err' });
        if (background) setRefreshPending(true);
      }
      return false;
    } finally {
      if (request === loadRequest.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [projectId, rights.view]);

  useEffect(() => {
    if (open) void load();
    return () => { loadRequest.current += 1; };
  }, [open, load]);

  useEffect(() => {
    const entering = active && !entry.current.active;
    const changed = refreshKey !== entry.current.refreshKey;
    entry.current = { active: open && active, refreshKey };
    if (!workspace || !open || !active || (!entering && !changed)) return;
    if (loadedProject.current !== projectId && !changed) return;
    if (latest.current.dirty || latest.current.busy) {
      setRefreshPending(true);
      return;
    }
    void load({ background: true });
  }, [active, refreshKey, workspace, open, projectId, load]);

  useEffect(() => {
    onDraftStateChange?.({
      dirty: !loading && dirty,
      busy: saving || savingBasis || approving || submitting || rejecting || assigning || assistantBusy || recoveringProposal,
    });
  }, [dirty, loading, saving, savingBasis, approving, assigning, assistantBusy, recoveringProposal, submitting, rejecting, onDraftStateChange]);



  useEffect(() => {
    if (!open || !active || !rights.view || meta?.status !== 'review') return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible' && !latest.current.dirty && !latest.current.busy) void load({ background: true });
    }, 20000);
    return () => window.clearInterval(timer);
  }, [open, active, rights.view, meta?.status, load]);

  if (!open || !rights.view) return null;

  const setMonthly = (i: number, patch: Partial<PricingMonthlyRow>) =>
    setSheet((p) => invalidatePricingSections({ ...p, monthly: p.monthly.map((r, j) => (j === i ? { ...r, ...patch } : r)) }, 'monthly'));
  const removeMonthly = (i: number) => setSheet((p) => invalidatePricingSections({ ...p, monthly: p.monthly.filter((_, j) => j !== i) }, 'monthly'));
  const addCustom = (preset?: PricingMonthlyRow) => setSheet((p) => invalidatePricingSections({ ...p, monthly: [...p.monthly, preset || { label: '', description: '', model: 'fixed', amount: null, price: null, overage: null }] }, 'monthly'));
  const cancelBasis = () => {
    const values = JSON.parse(savedBasis.current) as string[];
    setRateStr(values[0]); setPctStr(values[1]); setMiddleStr(values[2]); setSeniorStr(values[3]);
    setBasisOpen(false);
  };
  const saveBasis = async () => {
    if (savingBasis || assistantBusy) return;
    const rate = Number(rateStr), pct = Number(pctStr), middle = Number(middleStr), senior = Number(seniorStr);
    if ([rateStr, pctStr, middleStr, seniorStr].some(value => !value.trim()) ||
      !Number.isFinite(rate) || rate < 0 || rate > 100_000 ||
      !Number.isFinite(pct) || pct < 0 || pct > 100 ||
      !Number.isFinite(middle) || middle < 0 || !Number.isFinite(senior) || senior < 0) {
      setNote({ text: 'Enter valid non-negative rates.', kind: 'err' });
      return;
    }
    loadRequest.current += 1;
    setRefreshing(false);
    setSavingBasis(true);
    try {
      const r = await architectApi.putPricingMethodology({
        day_rate: rate, monthly_pct: pct, note: methodology?.note ?? null,
        rules: { ...rules, middle_week_rate: middle, senior_week_rate: senior },
      });
      if (latest.current.projectId !== projectId) return;
      setMethodology(r.methodology);
      const values = [String(r.methodology.day_rate), String(r.methodology.monthly_pct),
        String(r.methodology.rules?.middle_week_rate ?? DEFAULT_RULES.middle_week_rate),
        String(r.methodology.rules?.senior_week_rate ?? DEFAULT_RULES.senior_week_rate)];
      savedBasis.current = JSON.stringify(values);
      setRateStr(values[0]); setPctStr(values[1]); setMiddleStr(values[2]); setSeniorStr(values[3]);
      setBasisOpen(false);
      setNote({ text: 'Pricing rates saved.', kind: 'ok' });
    } catch (err: any) {
      if (err?.status === 409 || err?.status === 428) setSaveConflict(true);
      setNote({ text: err.message, kind: 'err' });
    } finally {
      setSavingBasis(false);
    }
  };

  const saveSnapshot = (draft: PricingSheet) => draft.plan?.workflow_version === 'lato-pricing-v2' || meta?.sheet?.plan?.workflow_version === 'lato-pricing-v2'
    ? architectApi.putPricing(projectId, draft, { review_revision: meta?.review_revision ?? 0, estimate_id: meta?.estimate_id ?? null })
    : architectApi.putPricing(projectId, draft);

  const save = async (): Promise<boolean> => {
    if (!rights.edit) return false;
    if (loadedProject.current !== projectId || latest.current.projectId !== projectId) return false;
    if (assistantBusy || recoveryPending.current) return false;
    if (basisDirty) {
      setNote({ text: 'Save the pricing basis before saving this draft.', kind: 'err' });
      return false;
    }
    if (!sheetDirty) return true;
    loadRequest.current += 1;
    setRefreshing(false);
    const revision = editRevision.current;
    setSaving(true);
    try {
      const r = await saveSnapshot(sheet);
      setMeta(r);
      setRepriceFailure(null);
      setRecoveryError(null);
      const normalized: PricingSheet = { ...(r.sheet || EMPTY), plan: r.sheet?.plan ?? plan };
      savedSheet.current = JSON.stringify(normalized);
      if (revision === editRevision.current) setSheetState(normalized);
      setNote({ text: 'Pricing draft saved.', kind: 'ok' });
      onChanged?.();
      return true;
    } catch (err: any) {
      if (err?.status === 409 || err?.status === 428) setSaveConflict(true);
      setNote({ text: err.message, kind: 'err' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (!rights.approve || (rights.configured && !meta?.approvals)) return;
    if (loadedProject.current !== projectId || latest.current.projectId !== projectId) return;
    if (assistantBusy || recoveryPending.current) return;
    if (basisDirty) {
      setNote({ text: 'Save the pricing basis before approval.', kind: 'err' });
      return;
    }
    setApproving(true);
    try {
      if (sheetDirty && !(await save())) return;
      const approvalBody = { ...(sheet.plan?.workflow_version === 'lato-pricing-v2' ? { review_revision: meta?.review_revision } : {}), ...pricingActor.body, ...(canManagePricing && !pricingActor.selected && !!meta?.assigned_to && meta.assigned_to !== currentUserId ? { override: true } : {}) };
      const r = await (Object.keys(approvalBody).length ? architectApi.approvePricing(projectId, approvalBody) : architectApi.approvePricing(projectId));
      setMeta(r);
      setRepriceFailure(r.reprice_error || null);
      setRecoveryError(null);
      const wrote = r.auto_started === 'proposal';
      const applied = !!r.repriced;
      setNote(r.reprice_error ? null : {
        text: r.approvals && r.approvals.given < r.approvals.required ? 'Your approval is recorded' : wrote ? 'Pricing approved — the proposal is being prepared automatically'
          : applied ? 'Pricing approved — review the revised proposal and its approval history'
            : 'Pricing approved',
        kind: 'ok',
      });
      onChanged?.();
      if ((wrote || applied) && onProceed) {
        if (proceedTimer.current !== null) window.clearTimeout(proceedTimer.current);
        proceedTimer.current = window.setTimeout(() => {
          proceedTimer.current = null;
          if (activeRef.current) proceedRef.current?.();
        }, 900);
      }
    } catch (err: any) {
      if (err?.code === 'pricing_incomplete' && Array.isArray(err?.body?.fields)) {
        const fields = err.body.fields as string[];
        const labels = fields.map(field => {
          const match = /^(setup|monthly)\.(\d+)\.price$/.exec(field);
          if (!match) return field;
          const index = Number(match[2]);
          return match[1] === 'setup'
            ? `Setup · ${sheet.setup[index]?.case || `case ${index + 1}`}`
            : `Monthly · ${sheet.monthly[index]?.label || `service ${index + 1}`}`;
        });
        setStep(fields[0]?.startsWith('monthly.') ? 'monthly' : 'setup');
        setNote({ text: `Set a price or an explicit TBD/Included decision for: ${labels.slice(0, 3).join(', ')}${labels.length > 3 ? ` (+${labels.length - 3} more)` : ''}.`, kind: 'err' });
      } else {
        if (err?.status === 409 || err?.status === 428) setSaveConflict(true);
      setNote({ text: err.message, kind: 'err' });
      }
    } finally {
      setApproving(false);
    }
  };

  const submit = async () => {
    if (!rights.edit) return;
    if (loadedProject.current !== projectId || latest.current.projectId !== projectId) return;
    if (latest.current.busy || basisDirty || !(meta?.can_submit ?? !!meta?.assigned_to)) return;
    if (!PRICING_REVIEW_STEPS.every(section => pricingSectionConfirmed(sheetRef.current, section))) {
      setStep(PRICING_REVIEW_STEPS.find(section => !pricingSectionConfirmed(sheetRef.current, section)) || 'development');
      setNote({ text: 'Confirm each pricing section before sending it for approval.', kind: 'err' });
      return;
    }
    setSubmitting(true);
    setNote(null);
    const revision = editRevision.current;
    try {
      let current = meta;
      if (sheetDirty || !meta.saved) {
        current = await saveSnapshot(sheetRef.current);
        const normalized = { ...(current.sheet || EMPTY), plan: current.sheet?.plan ?? plan };
        savedSheet.current = JSON.stringify(normalized);
        setMeta(current);
        if (revision !== editRevision.current) {
          setNote({ text: 'Your draft changed while saving. Review it before sending.', kind: 'err' });
          return;
        }
        setSheetState(normalized);
      }
      const result = await architectApi.submitPricing(projectId, { review_revision: current?.review_revision });
      setMeta(result);
      setNote({ text: `Sent to ${result.assigned_to_name || (result.approvals?.root_only ? 'Root' : 'the assigned approver')}.`, kind: 'ok' });
      onChanged?.();
    } catch (error: any) {
      if (error?.status === 409 || error?.status === 428) setSaveConflict(true);
      setNote({ text: error.message || 'Could not send pricing for approval.', kind: 'err' });
    } finally { setSubmitting(false); }
  };

  const requestChanges = async () => {
    if (!changesReason.trim() || rejecting || !meta) return;
    setRejecting(true);
    try {
      const result = await architectApi.requestPricingChanges(projectId, { reason: changesReason.trim(), review_revision: meta.review_revision });
      setMeta(result);
      setChangesOpen(false);
      setChangesReason('');
      setNote({ text: 'Changes requested. The author has been notified.', kind: 'ok' });
      onChanged?.();
    } catch (error: any) { if (error?.status === 409 || error?.status === 428) setSaveConflict(true); setNote({ text: error.message || 'Could not request changes.', kind: 'err' }); }
    finally { setRejecting(false); }
  };

  const assignOwner = async (userId: string) => {
    if (globalAssignment || !canManagePricing) return;
    if (dirty) {
      setNote({ text: 'Save your pricing draft before changing its approver.', kind: 'err' });
      return;
    }
    setAssigning(true);
    try {
      const r = await architectApi.assignPricing(projectId, userId);
      setMeta(r);
      onChanged?.();
    } catch (err: any) {
      if (err?.status === 409 || err?.status === 428) setSaveConflict(true);
      setNote({ text: err.message, kind: 'err' });
    } finally {
      setAssigning(false);
    }
  };

  const pricingApproved = meta?.status === 'approved' || (meta?.status === undefined && meta?.priced === true);
  const recoverProposal = async () => {
    if (!canManagePricing || !repriceFailure || !pricingApproved || loading || refreshing
      || latest.current.busy || latest.current.dirty || recoveryPending.current) return;
    if (!window.confirm('Create a new proposal from the approved estimate and prices? Your manually edited version will stay in history; its edits will not be copied into the new proposal.')) return;
    recoveryPending.current = true;
    setRecoveringProposal(true);
    setRecoveryError(null);
    try {
      await proposalApi.generateProposal(projectId);
    } catch (error: any) {
      setRecoveryError(error.message || 'Could not start the new proposal. Try again.');
      return;
    } finally {
      recoveryPending.current = false;
      setRecoveringProposal(false);
    }
    setRepriceFailure(null);
    setNote({ text: 'The new proposal is being prepared. Your edited version stays in history.', kind: 'ok' });
    onChanged?.();
    onProceed?.();
  };
  const assignedToOther = !!(meta?.assigned_to && currentUserId && meta.assigned_to !== currentUserId);
  const v2 = sheet.plan?.workflow_version === 'lato-pricing-v2';
  const reviewConfirmed = PRICING_REVIEW_STEPS.every(section => pricingSectionConfirmed(sheet, section));
  const actionBusy = !projectReady || loading || saving || savingBasis || approving || submitting || rejecting || assigning || refreshing || assistantBusy || recoveringProposal;
  const assignedToMe = !!currentUserId && meta?.assigned_to === currentUserId;
  const setupComplete = sheet.setup.length > 0 && (casePricingEnabled(sheet.plan) ? caseSaleTotal(sheet.plan) != null : v2 ? sheet.plan?.sale_price != null : sheet.setup.every(row => !!row.price?.trim()));
  const monthlyComplete = monthlyRowsReady(sheet.monthly, rules);
  const draftComplete = setupComplete && monthlyComplete;
  const approvalAllowed = rights.approve && (meta?.approvals ? pricingActor.canApprove : !rights.configured && !!meta?.assigned_to && (assignedToMe || (canManagePricing && pricingActor.canApprove)));
  const canApprovePricing = approvalAllowed && draftComplete && !meta?.stale && !actionBusy && !basisDirty && (!pricingApproved || dirty)
    && (!v2 || (meta?.status === 'review' && !dirty));
  const canSubmit = rights.edit && draftComplete && !meta?.stale && !actionBusy && !basisDirty && (meta?.can_submit ?? !!meta?.assigned_to) && reviewConfirmed && (!pricingApproved || dirty) && (meta?.status !== 'review' || dirty);
  const submitAction = v2 && (meta?.status !== 'review' || dirty);
  const saveImpact = pricingApproved && !dirty
    ? `Approved${meta?.approved_by_name ? ' by ' + meta.approved_by_name : ''}${meta?.approved_at ? ' · ' + new Date(meta.approved_at).toLocaleString() : ''}`
    : v2 && (!reviewConfirmed || !draftComplete) ? 'Review pricing'
    : meta?.status === 'review' ? `Awaiting ${meta.assigned_to_name || (meta.approvals?.root_only ? 'Root' : 'the assigned approver')}`
    : assignedToOther && meta?.assigned_to_name ? `Send to ${meta.assigned_to_name} when ready`
      : meta?.assigned_to && currentUserId === meta.assigned_to ? 'Ready for your approval'
        : meta?.approvals?.root_only ? 'Root reviews this stage' : 'Choose a Pricing approver in Approvers.';


  const stageDecision = projectReady ? (
    <StageFooter className="pricing-review-decision" placement={workspace ? "header" : "footer"} stage="pricing" pending={!pricingApproved || dirty} status={meta?.approvals} userId={currentUserId} count={meta?.approvals ? { given: meta.approvals.given, required: meta.approvals.required } : { given: pricingApproved && !dirty ? 1 : 0, required: 1 }} title={pricingApproved && !dirty ? 'Pricing approved' : meta?.status === 'review' && !dirty ? 'Ready for review' : 'Pricing draft'} hint={(pricingApproved && !dirty) || saveImpact === 'Review pricing' ? undefined : saveImpact}>
          {!workspace && canManagePricing && meta?.status !== 'approved' ? <ApproveAsSelector actor={pricingActor} disabled={actionBusy || dirty}/> : null}
          <button hidden={!rights.edit} type="button" className="arch-btn arch-btn-outline" disabled={actionBusy || !dirty} onClick={() => void save()}><Save size={15}/>{saving ? 'Saving…' : 'Save draft'}</button>
          {meta?.status === 'review' && approvalAllowed && !dirty ? <button type="button" className="arch-btn arch-btn-ghost" disabled={actionBusy} onClick={() => setChangesOpen(true)}>Request changes</button> : null}
          {(workspace && pricingApproved && !dirty) || (submitAction ? !rights.edit : !rights.approve) ? null : <button type="button" className="arch-btn arch-btn-primary" disabled={submitAction ? !canSubmit : !canApprovePricing} aria-describedby={saveImpactId} onClick={() => void (submitAction ? submit() : approve())}>{submitting ? 'Sending…' : approving ? 'Approving…' : submitAction ? 'Send for approval' : architectureReviewView(meta?.approvals, 'pricing')?.action || 'Approve pricing'}</button>}
        </StageFooter>
  ) : null;

  return (
    <div {...comments.surface} className={workspace ? 'arch-pr-workspace' : 'arch-modal-overlay'} role={workspace ? 'region' : 'dialog'} aria-modal={workspace ? undefined : true} aria-label="Pricing" hidden={workspace && !active}>
      <div className={workspace ? 'arch-pr-workspace-panel arch-pr' : 'arch-modal arch-pricing-modal arch-pr'}>
        {workspace ? <StageHeader className="pricing-review-header" title="Pricing" status={!projectReady ? (loading ? 'Loading' : 'Unavailable') : pricingApproved && !dirty ? 'Approved' : dirty ? 'Unsaved changes' : meta?.status === 'review' ? 'In review' : 'Draft'} actions={[
          ...(rights.edit ? [{ id: 'monthly-help', label: 'Ask Lato AI', icon: <SlidersHorizontal size={17}/>, disabled: !projectReady || loading || !sheet.setup.length, onClick: () => { setStep('monthly'); setMonthlyVisited(true); setSuggestionsOpen(true); } }] : []),
          ...(onToggleReference ? [{ id: 'reference', label: referenceOpen ? 'Hide estimate' : 'Show estimate', icon: <FileText size={17}/>, onClick: onToggleReference }] : []),
          ...(canManagePricing && (!globalAssignment || pricingActor.choices.length > 0) ? [{ id: 'approver', label: globalAssignment ? 'Approve as' : 'Approval settings', icon: <UserRound size={17}/>, disabled: !projectReady, onClick: () => setOwnerMenuOpen(true) }] : []),
          ...(canManagePricing ? [{ id: 'rates', label: 'Pricing rates', icon: <SlidersHorizontal size={17}/>, disabled: !projectReady, onClick: () => { setStep('development'); setBasisOpen(true); } }] : []),
        ]} decision={<>{stageDecision}<span id={saveImpactId} className="pricing-review-accessible-hint">{projectReady ? saveImpact : ''}</span></>}/> : <header className="arch-modal-head"><h2>Pricing — {projectName}</h2><button className="arch-icon-btn" onClick={onClose} aria-label="Close">✕</button></header>}

        <div className={workspace && reference ? 'arch-pricing-workspace-split' + (!referenceOpen ? ' is-reference-hidden' : '') : 'arch-pr-content'}>
        {workspace ? reference : null}
        <div ref={bodyRef} className="arch-modal-body" aria-busy={loading || refreshing}>
          {loading ? <p className="arch-muted arch-pr-empty" role="status">Loading pricing…</p> : null}
          {projectReady && !loading && (saveConflict || (workspace && (refreshing || refreshPending))) ? (
            <div className="arch-pr-refresh" role="status">
              {refreshing ? <span>Updating prices from the latest estimate…</span> : (
                <>
                  <span>{confirmRefresh ? 'Discard your unsaved pricing edits and load current pricing?' : saveConflict ? 'Current pricing changed. Your edits are kept here.' : 'The estimate may have changed. Your pricing edits are kept here.'}</span>
                  <div className="arch-pr-refresh-actions">
                    {confirmRefresh ? <button type="button" className="arch-btn arch-btn-ghost arch-btn-sm" onClick={() => setConfirmRefresh(false)}>Keep editing</button> : null}
                    <button type="button" className="arch-btn arch-btn-sm" disabled={saving || savingBasis} onClick={() => {
                      if (dirty && !confirmRefresh) setConfirmRefresh(true);
                      else void load({ background: true });
                    }}>{confirmRefresh ? 'Discard edits and reload' : saveConflict ? 'Reload current pricing' : 'Refresh prices'}</button>
                  </div>
                </>
              )}
            </div>
          ) : null}
          {projectReady && !loading && meta && meta.setup_empty ? (
            <p className="arch-muted arch-pr-empty">The price rows come from the approved estimate — one per case. Approve an estimate first and they will appear here.</p>
          ) : null}
          {projectReady && !loading && meta && meta.stale ? (
            <p className="arch-warn">The estimate changed after these prices were set — check the rows below.</p>
          ) : null}

          {projectReady && !loading && meta?.rejection_reason ? <div className="pricing-returned" role="status"><strong>Changes requested{meta.rejected_by_name ? ` by ${meta.rejected_by_name}` : ''}</strong><p>{meta.rejection_reason}</p></div> : null}
          {projectReady && !loading && sheet.setup.length ? <PricingReviewFlow
            sheet={sheet} rules={rules} step={step} onStepChange={next => { setStep(next); if (next === 'monthly') setMonthlyVisited(true); }}
            onChange={setSheet} disabled={actionBusy || !rights.edit} finalPriceView={!canManagePricing}
            approved={pricingApproved && !dirty}
            sourceOpen={sourceOpen} settingsOpen={basisOpen}
            caseFocusPath={caseFocus?.path} caseFocusToken={caseFocus}
            caseAttributes={(row, index) => commentBlockAttributes(comments.anchor(`setup.${index}`, row.case, `${row.case}: ${row.blurb || ''}`)) as React.HTMLAttributes<HTMLDivElement>}
            caseDetails={(row, index) => <CommentBlockButton anchor={comments.anchor(`setup.${index}`, row.case, `${row.case}: ${row.blurb || ''}`)} />}
            settings={<div className="arch-pr-basis">
                {basisOpen ? (
                  <div className="arch-pr-basis-form">
                    <label>Day rate <input className="arch-pr-in n" value={rateStr} onChange={(e) => editBasis(setRateStr, e.target.value)} /></label>
                    <label>Middle $/wk <input className="arch-pr-in n" value={middleStr} onChange={(e) => editBasis(setMiddleStr, e.target.value)} /></label>
                    <label>Senior $/wk <input className="arch-pr-in n" value={seniorStr} onChange={(e) => editBasis(setSeniorStr, e.target.value)} /></label>
                    <div className="pricing-flow-rate-actions">
                      <button className="arch-btn arch-btn-primary arch-btn-sm" disabled={savingBasis} onClick={() => void saveBasis()}>{savingBasis ? 'Saving…' : 'Save rates'}</button>
                      <button className="arch-btn arch-btn-ghost arch-btn-sm" disabled={savingBasis} onClick={cancelBasis}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button type="button" className="arch-pr-basis-btn" disabled={!canManagePricing} onClick={() => setBasisOpen(true)}>
                    <SlidersHorizontal size={15} /> Pricing rates
                  </button>
                )}
              </div>}
          >
          {projectReady && !loading && sheet.setup.length && monthlyVisited ? <>
            {!workspace && saveImpact ? <p id={saveImpactId} className="arch-pr-workspace-save-impact">{saveImpact}</p> : null}
            <MonthlyServicesEditor key={projectId} rows={sheet.monthly} rules={rules} disabled={actionBusy || !rights.edit}
              caseNames={sheet.setup.map(row => row.case)} onChange={setMonthly} onRemove={removeMonthly} onAdd={addCustom}
              rowAttributes={(row, index) => commentBlockAttributes(comments.anchor(`monthly.${index}`, row.label, `${row.label}: ${row.description || ''}`)) as React.HTMLAttributes<HTMLElement>}
              rowAction={(row, index) => <CommentBlockButton anchor={comments.anchor(`monthly.${index}`, row.label, `${row.label}: ${row.description || ''}`)} />} />
          </> : null}
          {rights.edit && projectReady && !loading && loadedProject.current === projectId && sheet.setup.length && monthlyVisited ? <div className="arch-pr-recommendation-wrap" hidden={step !== 'monthly'}>
            <MonthlyRecommendationPanel expanded={!sheet.monthly.length || suggestionsOpen} onExpand={() => setSuggestionsOpen(true)} onCollapse={sheet.monthly.length ? () => setSuggestionsOpen(false) : undefined} key={`${projectId}:${monthlySourceKey}`} projectId={projectId} sheet={sheet} disabled={saving || savingBasis} onBusyChange={setAssistantBusy} onApply={(rows, expectedSnapshot) => {
                if (JSON.stringify(sheetRef.current) !== expectedSnapshot) return false;
                setSuggestionsOpen(false);
                setSheet(current => invalidatePricingSections({ ...current, monthly: rows }, 'monthly'));
                return true;
              }} />
          </div> : null}

          </PricingReviewFlow> : null}

          {projectReady && repriceFailure ? <div className="arch-error" role="alert">
            <p>Pricing approved. Your edited proposal stays in history. A new version can be created from the approved estimate and prices.</p>
            {recoveryError ? <p>{recoveryError}</p> : null}
            {canManagePricing ? <button type="button" className="arch-btn arch-btn-primary" disabled={recoveringProposal || loading || refreshing || latest.current.busy || dirty || !pricingApproved} onClick={() => void recoverProposal()}>{recoveringProposal ? 'Starting…' : 'Create new proposal'}</button> : null}
          </div> : null}
          {note ? <p className={note.kind === 'ok' ? 'arch-ok' : 'arch-error'} role={note.kind === 'ok' ? 'status' : 'alert'}>{note.text}</p> : null}
        </div>
        </div>

        {rights.edit && <StageAiButton disabled={!projectReady || loading || !sheet.setup.length} busy={assistantBusy} onClick={() => {
          setStep('monthly'); setMonthlyVisited(true); setSuggestionsOpen(true);
          window.setTimeout(() => bodyRef.current?.querySelector('.arch-monthly-rec')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
        }}/>}
        {!workspace ? stageDecision : null}
      </div>
      {projectReady && ownerMenuOpen ? <div className="arch-modal-overlay stage-owner-overlay" role="dialog" aria-modal="true" aria-label="Pricing approver"><div className="stage-owner-dialog"><StageHeader title={globalAssignment ? "Approve as" : "Approval settings"} actions={[{ id: 'close', label: 'Close', onClick: () => setOwnerMenuOpen(false) }]}/>{!globalAssignment && <label>Send pricing to<select aria-label="Pricing approver" value={meta?.assigned_to || ''} disabled={actionBusy || dirty} onChange={event => void assignOwner(event.target.value)}><option value="" disabled>Choose approver</option>{owners.map(owner => <option key={owner.id} value={owner.id}>{owner.displayName || owner.username}</option>)}</select></label>}{canManagePricing && meta?.status !== 'approved' ? <div className="pricing-approve-as-setting"><ApproveAsSelector actor={pricingActor} disabled={actionBusy || dirty}/></div> : null}<button type="button" className="arch-btn arch-btn-primary" onClick={() => setOwnerMenuOpen(false)}>Done</button></div></div> : null}
      {projectReady && changesOpen ? <div className="arch-modal-overlay pricing-changes-overlay" role="dialog" aria-modal="true" aria-label="Request pricing changes">
        <div className="pricing-changes-dialog"><header><h2>Request changes</h2><button type="button" aria-label="Close changes" disabled={rejecting} onClick={() => setChangesOpen(false)}>×</button></header>
          <label>What needs to change?<textarea autoFocus rows={4} maxLength={2000} value={changesReason} disabled={rejecting} onChange={event => setChangesReason(event.target.value)} placeholder="Explain what the author should adjust." /></label>
          <footer><button type="button" className="arch-btn arch-btn-ghost" disabled={rejecting} onClick={() => setChangesOpen(false)}>Cancel</button><button type="button" className="arch-btn arch-btn-primary" disabled={rejecting || !changesReason.trim()} onClick={() => void requestChanges()}>{rejecting ? 'Sending…' : 'Send feedback'}</button></footer>
        </div></div> : null}
    </div>
  );
}
